import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Typography from '@material-ui/core/Typography';
import withRouteContext from 'tuc-webui-base/components/utils/withRouteContext';
import createContextFilter from '../../services/Filter';
import CardGroup from './CardGroup';
import './CardList.scss';

const GetId = o => o.id;
const GetDomId = id => `card-${id}`;

const CardList = (props) => {
  const {
    cardObjects,
    className,
    cardComponent: Component,
    renderComponent,
    getFilterValues,
    getId,
    getDomId,
    rowCount,
    getGroups,
    compare,
    newlyCreated,
    emptyMessage,
    column,
    ...rest
  } = props;

  const newlyCreatedId = newlyCreated && newlyCreated.length && getId(newlyCreated[0]);

  // Filter objects
  const filter = getFilterValues
    ? createContextFilter(props.context, getFilterValues) : undefined;
  const filteredObjects = filter ? cardObjects.filter(filter) : cardObjects;

  useEffect(() => {
    if (rowCount) {
      rowCount.setFilteredRowCount(filteredObjects.length);
    }
  }, [filteredObjects.length]);

  //
  // Scroll to newly created items
  //

  useEffect(() => {
    // const elt = newlyCreatedId && refs.current[newlyCreatedId] && refs.current[newlyCreatedId].current;
    const elt = newlyCreatedId && document.getElementById(getDomId(newlyCreatedId));
    if (elt && elt.scrollIntoView) {
      elt.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [newlyCreatedId]);

  if (!filteredObjects.length) {
    if (!emptyMessage) {
      return (null);
    }
    return (
      <div className={classnames('grb-cards', className)}>
        <Typography variant="h5" className="grb-cards-empty-message">{emptyMessage}</Typography>
      </div>
    );
  }

  const groups = getGroups ? getGroups(filteredObjects) : [{ objects: filteredObjects }];

  // Render card list
  const renderList = (lst) => {
    const slst = compare ? lst.sort(compare) : lst;
    if (!slst.length) {
      return (
        <div className={classnames('grb-cards', className)}>
          <Typography variant="h5" className="grb-cards-empty-message">{emptyMessage}</Typography>
        </div>
      );
    }
    if (Component) {
      return (
        <div className={classnames('grb-cards', className, column && 'grb-cards-column')}>
          {slst.map((object) => {
            const id = getId(object);
            return (
              <Component
                {...rest}
                key={id}
                id={getDomId(id)}
                object={object}
                newlyCreated={newlyCreatedId === id}
              />
            );
          })}
        </div>
      );
    }
    if (renderComponent) {
      return (
        <div className={classnames('grb-cards', className, column && 'grb-cards-column')}>
          {slst.map((object) => {
            const id = getId(object);
            return renderComponent({
              ...rest,
              object,
              id: getDomId(id),
              newlyCreated: newlyCreatedId === id,
            });
          })}
        </div>
      );
    }
    return (null);
  };

  if ((groups.length === 1) && !groups[0].title) {
    return renderList(groups[0].objects);
  }

  return (
    <div className="grb-card-groups">
      {groups.map(g => (
        <CardGroup {...props} {...g} key={g.id}>
          {renderList(g.objects)}
        </CardGroup>
      ))}
    </div>
  );
};

CardList.propTypes = {
  className: PropTypes.string,
  cardObjects: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  newlyCreated: PropTypes.arrayOf(PropTypes.shape({})),
  cardComponent: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
  ]),
  renderComponent: PropTypes.func,
  getFilterValues: PropTypes.func,
  getId: PropTypes.func,
  rowCount: PropTypes.shape({
    setFilteredRowCount: PropTypes.func,
  }),
  getGroups: PropTypes.func,
  compare: PropTypes.func,
  getDomId: PropTypes.func,
  GroupComponent: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({}),
  ]),
  emptyMessage: PropTypes.string,
  context: PropTypes.shape({}).isRequired,
  column: PropTypes.bool,
};

CardList.defaultProps = {
  className: undefined,
  cardComponent: undefined,
  renderComponent: undefined,
  getFilterValues: undefined,
  getId: GetId,
  getDomId: GetDomId,
  rowCount: undefined,
  getGroups: undefined,
  compare: undefined,
  GroupComponent: undefined,
  newlyCreated: undefined,
  emptyMessage: 'No items',
  column: false,
};

export default withRouteContext(CardList);
